import { adminFetch, buildListParams, normalizeList } from "./_client";
import type { AdminListing, StaleChangeRequestsResponse, CarNote, ListingDetail, AdminListParams, AdminListResponse, FlaggedHostProfilesResponse, HostProfileModerationDetail } from "./types";


export async function fetchAdminListings(token: string, params: AdminListParams = {}): Promise<AdminListResponse<AdminListing>> {
  const sp = buildListParams({ status: "awaiting_approval", ...params });
  const raw = await adminFetch<{ rows?: AdminListing[]; listings?: AdminListing[]; total: number; page: number; pageSize?: number; limit?: number }>(`/admin/listings?${sp}`, token);
  return normalizeList<AdminListing>(raw as Record<string, unknown>, "listings");
}


export function fetchAdminListing(token: string, id: number) {
  return adminFetch<ListingDetail>(`/admin/listings/${id}`, token);
}

export function reviewPausedListing(token: string, id: number, revision: string, action: "approve" | "request_changes", notes?: string) {
  return adminFetch<{ success: boolean; listing_status: string; resolved_log_ids: number[]; warnings?: string[] }>(
    `/admin/listings/${id}/review-current-content`, token, {
      method: "POST", body: JSON.stringify({ revision, action, notes }),
    }, false);
}


export function moderateListing(token: string, id: number, action: string, reason?: string, notes?: string, review?: { id: number; revision: string } | null, currentReviewRevision?: string) {
  return adminFetch<{ success: boolean; searchSyncPending?: boolean; warnings?: string[] }>(`/admin/listings/${id}/moderate`, token, {
    method: "POST",
    body: JSON.stringify({ action, reason, notes, reviewRequestId: review?.id, reviewRevision: review?.revision, currentReviewRevision }),
  }, false);
}


/**
 * Task #2256 — admin parity for the 4 Slack hold-the-change actions.
 * Resolves the currently open held_change row on the listing.
 */
export function resolveHeldChange(
  token: string,
  id: number,
  action: "approve_change" | "reject_change" | "edit_and_approve" | "request_owner_fix",
  opts?: { reason?: string; fieldOverrides?: Record<string, string>; proposalId?: number; proposalRevision?: string },
) {
  return adminFetch<{ success: boolean; action: string }>(`/admin/listings/${id}/held-change`, token, {
    method: "POST",
    body: JSON.stringify({ action, ...opts }),
  });
}


export function fetchFlaggedHostProfiles(token: string, page = 1) {
  return adminFetch<FlaggedHostProfilesResponse>(`/admin/host-profiles/flagged?page=${page}`, token);
}


// Task #6333 — moderation review detail for one user (live content + open
// logs + resolved history), backing the ProfileModerationReviewDialog.
export function fetchHostProfileModerationDetail(token: string, userId: string) {
  return adminFetch<HostProfileModerationDetail>(`/admin/host-profiles/${userId}/moderation`, token);
}


export function moderateHostProfile(token: string, userId: string, action: "approve" | "reject", notes?: string) {
  return adminFetch<{ success: boolean; action: string }>(`/admin/host-profiles/${userId}/moderate`, token, {
    method: "POST",
    body: JSON.stringify({ action, notes }),
  });
}



export function retryListingAiScan(token: string, id: number, proposal?: { proposalId: number; proposalRevision: string } | { scanLogId: number } | null) {
  return adminFetch<{ success: boolean; triggered_at: string; baseline_log_id: number | null }>(`/admin/listings/${id}/retry-ai-scan`, token, {
    method: "POST",
    body: JSON.stringify(proposal ?? {}),
  });
}


// Task #1750 — durable rerun. Routes through dispatchCarModeration so the
// scan goes through the breaker / scan_pending / retry-sweeper pipeline.
// Used by the admin "Re-run AI" button on dispatch_failed listings.
export function rerunListingAiModeration(token: string, id: number, proposal?: { proposalId: number; proposalRevision: string } | null) {
  return adminFetch<{ success: boolean; dispatched: boolean; triggered_at: string; baseline_log_id: number | null }>(`/admin/listings/${id}/rerun-moderation`, token, {
    method: "POST",
    body: JSON.stringify(proposal ?? {}),
  });
}


/**
 * Trigger admin-side Apex Score regeneration for a listing. Calls
 * POST /ai-enrichment/admin-run which runs the full enrichment pipeline
 * (spec + apex + valuation) under admin auth, bypassing user ownership checks.
 */
export function regenerateApexScore(token: string, carId: number) {
  return adminFetch<{ success: boolean; message: string }>(`/ai-enrichment/admin-run`, token, {
    method: "POST",
    body: JSON.stringify({ carId }),
  });
}


export async function fetchStaleChangeRequests(token: string): Promise<StaleChangeRequestsResponse> {
  return adminFetch<StaleChangeRequestsResponse>(`/admin/listings/stale-change-requests`, token);
}


export async function sendChangeRequestReminder(token: string, carId: number): Promise<{ success: boolean }> {
  return adminFetch<{ success: boolean }>(`/admin/listings/${carId}/send-change-request-reminder`, token, {
    method: "POST",
  });
}


export function updateListingFields(token: string, id: number, fields: Record<string, unknown>) {
  return adminFetch<{
    success: boolean;
    changes: Record<string, unknown>;
    moderation?: { status: "scan_pending" | "pending_manual_review" | "not_needed"; log_id: number | null; fields: string[] };
    apex_regeneration?: {
      status: "queued" | "failed";
      fields: string[];
      message?: string;
    };
  }>(`/admin/listings/${id}/fields`, token, {
    method: "PATCH",
    body: JSON.stringify({ fields }),
  });
}


export function toggleListingFlag(token: string, id: number, flag: string, value: boolean) {
  return adminFetch<{ success: boolean; flag: string; value: boolean }>(`/admin/listings/${id}/flags`, token, {
    method: "PATCH",
    body: JSON.stringify({ flag, value }),
  });
}


export function updateListingDelivery(token: string, id: number, fields: Record<string, unknown>) {
  return adminFetch<{ success: boolean; changes: Record<string, unknown> }>(`/admin/listings/${id}/delivery`, token, {
    method: "PATCH",
    body: JSON.stringify({ fields }),
  });
}


export function fetchPricingConfig(token: string, id: number) {
  return adminFetch<{ pricing_config: Record<string, number> | null }>(`/admin/listings/${id}/pricing-config`, token);
}


export function savePricingConfig(token: string, id: number, config: Record<string, number>) {
  return adminFetch<{ success: boolean }>(`/admin/listings/${id}/pricing-config`, token, {
    method: "PUT",
    body: JSON.stringify({ config }),
  });
}


export function fetchCarNotes(token: string, id: number) {
  return adminFetch<{ notes: CarNote[] }>(`/admin/listings/${id}/notes`, token);
}


export function addCarNote(token: string, id: number, note: string) {
  return adminFetch<CarNote>(`/admin/listings/${id}/notes`, token, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}


export function updateCarNote(token: string, id: number, noteId: number, note: string) {
  return adminFetch<CarNote>(`/admin/listings/${id}/notes/${noteId}`, token, {
    method: "PATCH",
    body: JSON.stringify({ note }),
  });
}


export function deleteCarNote(token: string, id: number, noteId: number) {
  return adminFetch<{ success: boolean }>(`/admin/listings/${id}/notes/${noteId}`, token, {
    method: "DELETE",
  });
}
